import { Alert, DatePicker, Form, FormInstance, Input, InputNumber, Select, Switch } from 'antd';
import { FormSelect } from '../form/FormBasic';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import { priceService } from '../../api/services/priceService';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { getTranslatedMetricOptions } from '../../enums/PriceAlertConfigTriggeringPriceEnum';
import { PriceAlertConfigConditionOptions } from '../../enums/PriceAlertConfigConditionEnum';
import { ManualPriceConditionOptions } from '../../enums/ManualPriceConditionEnum';
import { useUserContext } from '../../contexts/UserContext';

interface IAlertConfig {
  product: string;
  triggeringPrice: string;
  priceManual?: string;
  dateRange?: string;
  condition: string;
  threshold: string;
  department: string;
  municipality: string;
  enableAlert: boolean;
}

interface AddPriceAlertConfigProps {
  form: FormInstance;
  alertConfig: IAlertConfig;
  handleCreate: () => void;
  handleCancel: () => void;
  isModal?: boolean;
  getConfirmation?: () => void;
  isEdit?: boolean;
}

const AddPriceAlertConfig: React.FC<AddPriceAlertConfigProps> = ({ form, getConfirmation }) => {
  const { t } = useTranslation('priceAlertsConfiguration');

  const [mainProductOptions, setMainProductOptions] = useState<any>();
  const [presentationOptions, setPresentationOptions] = useState<any>();
  const [departmentsOptions, setDepartmentsOptions] = useState<any>();
  const [municipalityOptions, setMunicipalityOptions] = useState<any>();
  const [selectedMainProducts, setSelectedMainProducts] = useState<number[]>([]);
  const isInitializingRef = useRef(true);
  const formRef = useRef(form);
  const [isMunicipalityRequired, setIsMunicipalityRequired] = useState(false);

  // Update form ref when form changes
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const [showManualPrice, setShowManualPrice] = useState<boolean>(false);
  const [showAveragePrice, setShowAveragePrice] = useState<boolean>(false);
  const [isLoadingAveragePrice, setIsLoadingAveragePrice] = useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined);

  const [authStatus, setAuthStatus] = useState<{ isAuthenticated: boolean; token: string | null }>({
    isAuthenticated: false,
    token: null,
  });

  const { IsAuthenticated, getUserToken } = useUserContext();

  useEffect(() => {
    const token = getUserToken();
    const isAuth = IsAuthenticated();
    setAuthStatus({ isAuthenticated: isAuth, token });
  }, [IsAuthenticated, getUserToken]);

  useEffect(() => {
    getMainProductsList();
    getLocations();
    loadProductsForSelection(); // Load all products initially (same as AddTaskForm)
    isInitializingRef.current = false;
  }, []);

  const mainProducts = useWatch('mainProducts', form);
  const productRefId = useWatch('productRefId', form);
  const department = useWatch('department', form);
  const municipality = useWatch('municipality', form);
  const dateRange = useWatch('dateRange', form);

  useEffect(() => {
    if (
      showAveragePrice &&
      productRefId &&
      selectedProductId &&
      dateRange &&
      Array.isArray(dateRange) &&
      dateRange.length === 2
    ) {
      calculateAveragePrice();
    }
  }, [productRefId, selectedProductId, department, municipality, dateRange, mainProducts, showAveragePrice]);

  const getTranslatedAverageConditionOptions = () => {
    return PriceAlertConfigConditionOptions.map((option) => {
      const translatedLabel = t(option.label);
      return {
        ...option,
        label: translatedLabel,
      };
    });
  };

  const getTranslatedManualConditionOptions = () => {
    return ManualPriceConditionOptions.map((option) => {
      const translatedLabel = t(option.label);
      return {
        ...option,
        label: translatedLabel,
      };
    });
  };

  const getMainProductsList = async () => {
    const mainProductsData = await referenceDataSyncService.getMainProducts();

    const optionArr = mainProductsData.map((mp) => ({
      label: mp?.name,
      value: mp?.id,
      id: mp?.id,
    }));
    setMainProductOptions(optionArr);
  };

  const onMainProductChange = (selectedValues: number[]) => {
    setTimeout(() => {
      formRef.current.setFieldsValue({
        product: undefined,
        mainProducts: selectedValues,
      });
    }, 0);

    setSelectedMainProducts(selectedValues);
  };

  const loadProductsForSelection = useCallback(async () => {
    try {
      let presentations = await referenceDataSyncService.getPresentations();

      if (selectedMainProducts && selectedMainProducts.length > 0) {
        presentations = presentations.filter((p: any) => selectedMainProducts.includes(p.mainProductId));
      }

      const productOptionArr = presentations.map((presentation: any) => ({
        label: presentation?.name,
        value: presentation?.refId,
        id: presentation?.refId,
        productNumericId: presentation?.id,
      }));

      setPresentationOptions(productOptionArr);
    } catch (error) {
      console.error('Error loading products for selection:', error);
      setPresentationOptions([]);
    }
  }, [selectedMainProducts]);

  useEffect(() => {
    if (!isInitializingRef.current) {
      loadProductsForSelection();
    }
  }, [selectedMainProducts]);

  const onProductChange = (_selectedValue: any, option: any) => {
    setSelectedProductId(option?.productNumericId);
    setTimeout(() => {
      formRef.current.setFieldValue('productRefId', option?.id);
    }, 0);
  };

  const getLocations = async () => {
    const locationData = await referenceDataSyncService.getLocations();
    const departmentOptionArr = locationData.map((location) => ({
      label: location?.district,
      value: location?.district,
      id: location?.id,
    }));
    const uniqueDepartmentOptionArr = removeDuplicate(departmentOptionArr, 'value');
    setDepartmentsOptions(uniqueDepartmentOptionArr);
  };

  const removeDuplicate = (arr: any, property: any) => {
    return [...new Map(arr.map((item: { [x: string]: any }) => [item[property], item])).values()];
  };

  const disableFutureDates = (current: any) => {
    return current && current > dayjs().endOf('day');
  };

  const handleTriggeringPrice = (val: any) => {
    switch (val) {
      case 'manual_price':
        setShowManualPrice(true);
        setShowAveragePrice(false);
        form.setFieldValue('dateRange', undefined);
        break;
      case 'average_price':
        setShowManualPrice(false);
        setShowAveragePrice(true);
        form.setFieldValue('dateRange', undefined);
        break;
      default:
        setShowManualPrice(false);
        setShowAveragePrice(false);
    }
  };

  const onDepartmemtChange = async (val: any) => {
    if (val) {
      setIsMunicipalityRequired(true);
      await loadMunicipalityForDepartment(val.toString());
    } else {
      setIsMunicipalityRequired(false);
      setMunicipalityOptions([]);
    }

    form.setFieldValue('municipality', undefined);
    form.setFieldValue('locationId', undefined);
  };

  const loadMunicipalityForDepartment = async (department: string) => {
    const municipalityData = await referenceDataSyncService.getLocationsByDistrict(department);

    const municipalityOptionArr = municipalityData.map((municipality: any) => ({
      label: municipality.city,
      value: municipality.city,
      id: municipality.id,
    }));

    const uniqueMunicipalityOptionArr = removeDuplicate(municipalityOptionArr, 'value');
    setMunicipalityOptions(uniqueMunicipalityOptionArr);
  };

  const onMunicipalityChange = async (val: any) => {
    if (val) {
      const department = formRef.current.getFieldValue('department');
      if (department) {
        try {
          const locationId = await referenceDataSyncService.getLocationId(department, val);
          // Use form ref to avoid circular reference
          setTimeout(() => {
            formRef.current.setFieldValue('locationId', locationId);
          }, 0);
        } catch {
          // Use form ref to avoid circular reference
          setTimeout(() => {
            formRef.current.setFieldValue('locationId', undefined);
          }, 0);
        }
      }
    } else {
      // Use form ref to avoid circular reference
      setTimeout(() => {
        formRef.current.setFieldValue('locationId', undefined);
      }, 0);
    }
  };

  const disablePastDates = (current: any) => {
    return current && current < dayjs().startOf('day');
  };

  const calculateAveragePrice = useCallback(async () => {
    if (!showAveragePrice || !selectedProductId || !dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) return;

    try {
      setIsLoadingAveragePrice(true);

      const requestParams: any = {
        productId: selectedProductId,
        fromDate: dateRange[0].valueOf(),
        toDate: dateRange[1].valueOf(),
        ...(department && { department }),
        ...(municipality && { city: municipality }),
      };

      const result = await priceService.getAveragePrice(requestParams);

      formRef.current.setFieldValue('avgPrice', result.averagePrice);
    } catch {
      console.error('Error calculating average price');
    } finally {
      setIsLoadingAveragePrice(false);
    }
  }, [showAveragePrice, selectedProductId, productRefId, department, municipality, dateRange]);

  return (
    <>
      {!authStatus.isAuthenticated && (
        <Alert
          message="Authentication Required"
          description="You need to be logged in to sync data with the server. Please log in to enable sync functionality."
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}
      <Form
        layout="vertical"
        form={form}
        name="addAlertConfigForm"
        onFinish={() => {
          if (getConfirmation) {
            getConfirmation();
          }
        }}
        className="mt-2 w-full"
        initialValues={{ enableAlert: true }}
      >
        <FormSelect
          name="department"
          label={t('departmentLabel')}
          rules={[{ required: false, message: t('departmentFieldRequired') }]}
          placeholder={t('departmentFieldPlaceholder')}
          disabled={false}
          isRequired={false}
          allowClear={true}
          options={departmentsOptions}
          onChange={onDepartmemtChange}
        />

        <FormSelect
          name="municipality"
          label={t('municipalityLabel')}
          rules={[{ required: isMunicipalityRequired, message: t('municipalityFieldRequired') }]}
          placeholder={t('municipalityFieldPlaceholder')}
          disabled={false}
          isRequired={false}
          allowClear={true}
          options={municipalityOptions}
          onChange={onMunicipalityChange}
        />

        <Form.Item name="locationId" label="Location ID" hidden>
          <Input hidden />
        </Form.Item>

        <Form.Item
          name="mainProducts"
          label={t('mainProductsLabel')}
        >
          <Select
            disabled={false}
            placeholder={t('mainProductsFieldPlaceholder')}
            options={mainProductOptions}
            mode="multiple"
            onChange={onMainProductChange}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <div style={{ display: 'flex', marginBottom: '8px' }}>
          {selectedMainProducts?.length > 0 && (
            <a
              className="ml-auto"
              onClick={() => {
                onMainProductChange([]);
                form.setFieldValue('mainProducts', []);
              }}
            >
              {t('deselectAll')}
            </a>
          )}
        </div>

        <Form.Item
          name="product"
          label={t('presentationLabel')}
          rules={[{ required: true, message: t('presentationFieldRequired') }]}
        >
          <Select
            disabled={false}
            placeholder={t('presentationFieldPlaceholder')}
            options={presentationOptions}
            allowClear={true}
            onChange={onProductChange}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item name="productRefId" label="Product RefID" hidden>
          <Input hidden />
        </Form.Item>

        {/* --- Triggering Condition --- */}
        <FormSelect
          name="triggeringPrice"
          label={t('metricLabel')}
          rules={[{ required: true, message: t('metricFieldRequired') }]}
          placeholder={t('metricFieldPlaceholder')}
          disabled={false}
          isRequired={false}
          allowClear={true}
          options={getTranslatedMetricOptions(t)}
          onChange={handleTriggeringPrice}
        />
        {showManualPrice && (
          <Form.Item
            name="dateRange"
            label={t('monitoringDateRangeLabel')}
            rules={[{ required: true, message: t('monitoringDateRangeFieldRequired') }]}
          >
            <DatePicker.RangePicker
              className="w-full"
              showTime={false}
              showSecond={false}
              format="YYYY-MM-DD"
              inputReadOnly={true}
            />
          </Form.Item>
        )}

        {showManualPrice && (
          <Form.Item
            name="price"
            label={t('priceLabel')}
            required={true}
            validateFirst
            rules={[
              {
                required: true,
                message: t('priceFieldRequired'),
              },
              {
                validator: (_, value) => {
                  if (value > 9999999999999.99) {
                    return Promise.reject(new Error(t('invalidInput')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              type="number"
              placeholder={t('priceFieldPlaceholder')}
              className="w-full"
              controls={false}
              onWheel={(e) => e.currentTarget.blur()}
              precision={2}
              min={0.01}
              onKeyDown={(e) => {
                // Allow one fullstop, 2 decimal place. digits allowed
                if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                  e.preventDefault();
                }
                if (e.key === '.' && String((e.target as HTMLInputElement).value).includes('.')) {
                  e.preventDefault();
                }
              }}
              formatter={(value) => {
                if (!value) return '';
                return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
              }}
            />
          </Form.Item>
        )}

        {showManualPrice && (
          <FormSelect
            name="manualPriceCondition"
            label={t('conditionLabel')}
            rules={[{ required: true, message: t('conditionFieldRequired') }]}
            placeholder={t('conditionFieldPlaceholder')}
            disabled={false}
            isRequired={false}
            allowClear={true}
            options={getTranslatedManualConditionOptions()}
          />
        )}

        {showAveragePrice && (
          <Form.Item
            name="dateRange"
            label={t('baseDateRangeLabel')}
            rules={[{ required: true, message: t('dateRangeFieldRequired') }]}
          >
            <DatePicker.RangePicker
              disabledDate={disableFutureDates}
              className="w-full"
              showTime={false}
              showSecond={false}
              format="YYYY-MM-DD"
              placeholder={[t('startDatePlaceholder'), t('endDatePlaceholder')]}
              inputReadOnly={true}
            />
          </Form.Item>
        )}

        {showAveragePrice && (
          <Form.Item name="avgPrice" label={t('baseAvgPriceLabel')} required={false}>
            <InputNumber
              type="number"
              readOnly
              disabled={true}
              placeholder={
                isLoadingAveragePrice ? t('baseAvgPlaceholderCalculating') : t('baseAvgPlaceholder')
              }
              className="w-full bg-gray-100 opacity-60"
              controls={false}
              precision={2}
              min={0.01}
              formatter={(value) => {
                if (!value) return '';
                return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
              }}
            />
          </Form.Item>
        )}

        {showAveragePrice && (
          <Form.Item
            name="monitoringDateRange"
            label={t('monitoringDateRangeLabel')}
            rules={[{ required: true, message: t('monitoringDateRangeFieldRequired') }]}
          >
            <DatePicker.RangePicker
              disabledDate={disablePastDates}
              className="w-full"
              showTime={false}
              showSecond={false}
              format="YYYY-MM-DD"
              placeholder={[t('startDatePlaceholder'), t('endDatePlaceholder')]}
              inputReadOnly={true}
            />
          </Form.Item>
        )}

        {showAveragePrice && (
          <FormSelect
            name="condition"
            label={t('conditionLabel')}
            rules={[{ required: true, message: t('conditionFieldRequired') }]}
            placeholder={t('conditionFieldPlaceholder')}
            disabled={false}
            isRequired={false}
            allowClear={true}
            options={getTranslatedAverageConditionOptions()}
          />
        )}

        {showAveragePrice && (
          <Form.Item
            name="threshold"
            label={t('thresholdLabel')}
            required={true}
            validateFirst
            rules={[
              {
                required: true,
                message: t('thresholdFieldRequired'),
              },
              {
                validator: (_, value) => {
                  // MAX_VALUE is 1.79E+308
                  // value > Number.MAX_VALUE
                  if (value > 9999999999999.99) {
                    return Promise.reject(new Error(t('invalidInput')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              type="number"
              placeholder={t('thresholdFieldPlaceholder')}
              className="w-full"
              controls={true}
              onWheel={(e) => e.currentTarget.blur()}
              precision={2}
              min={0.01}
              onKeyDown={(e) => {
                // Allow one fullstop, 2 decimal place. digits allowed
                if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                  e.preventDefault();
                }
                if (e.key === '.' && String((e.target as HTMLInputElement).value).includes('.')) {
                  e.preventDefault();
                }
              }}
              formatter={(value) => {
                if (!value) return '';
                return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
              }}
            />
          </Form.Item>
        )}

        {(showAveragePrice || showManualPrice) && (
          <Form.Item
            name="numberOfInstance"
            label={t('numberOfInstanceLabel')}
            required={true}
            validateFirst
            validateTrigger="onChange"
            rules={[
              {
                required: true,
                message: t('numberOfInstanceFieldRequired'),
              },
              {
                pattern: /^[0-9]*$/,
                message: t('invalidInput'),
              },
              {
                validator: (_, value) => {
                  if (value === null || value >= 1) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('instanceZeroError')));
                },
              },
              {
                validator: (_, value) => {
                  // MAX_SAFE_INTEGER is 9007199254740991
                  if (value > Number.MAX_SAFE_INTEGER) {
                    return Promise.reject(new Error(t('invalidInput')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              type="number"
              placeholder={t('numberOfInstanceFieldPlaceholder')}
              className="w-full"
              controls={true}
              onWheel={(e) => e.currentTarget.blur()}
              precision={0}
              min={0}
              onKeyDown={(e) => {
                if (
                  e.key === 'e' ||
                  e.key === 'E' ||
                  e.key === '-' ||
                  e.key === '+' ||
                  e.key === '.' ||
                  e.key === 'Decimal'
                ) {
                  e.preventDefault();
                }
                const allowedKeys = [
                  'Backspace',
                  'Enter',
                  'Escape',
                  'ArrowLeft',
                  'ArrowRight',
                  'Delete',
                  'End',
                  'Home',
                ];

                const isDigit = /[0-9]/.test(e.key);
                if (!allowedKeys && !isDigit) {
                  e.preventDefault();
                }
              }}
              formatter={(value) => `${value}`.replace(/\D/g, '')}
            />
          </Form.Item>
        )}

        <div className="flex items-center gap-6">
          <span className="font-semibold text-gray-700">{t('enableAlertLabel')}</span>

          <Form.Item
            name="enableAlert"
            label={t('enableAlertLabel')}
            labelCol={{ span: 0 }}
            wrapperCol={{ span: 24 }}
            required={false}
            rules={[
              {
                required: true,
                message: t('enableAlertFieldRequired'),
              },
            ]}
            valuePropName="checked"
          >
            <Switch className="mt-3" />
          </Form.Item>
        </div>
      </Form>
    </>
  );
};

export default AddPriceAlertConfig;
